import {
  DataGrid,
  GridColDef,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
  GridRowClassNameParams,
  GridPaginationModel,
  GridFeatureMode,
  GridSortModel,
  GridCallbackDetails,
} from '@mui/x-data-grid';
import { useWindowSize } from '@/hooks/useWindowSize';

export type RowData = {
  [index: string]: string | number;
};

type DataTableProps<T> = {
  columns: GridColDef[];
  rows: (RowData & T)[];
  pageSizeOptions?: number[];
  pageSize?: number;
  totalCount?: number;
  selectedRows?: string[];
  handleSelectionChange?: (selection: string[]) => void;
  handlePageChange?: (page: number) => void;
  handleRowsPerPageChange?: (rowsPerPage: number) => void;
  paginationMode?: GridFeatureMode;
  sortingMode?: GridFeatureMode;
  visibleColumns?: GridColumnVisibilityModel;
  setVisiblecolumns?: Function;
  selected?: boolean;
  onRowClick?: (row: T) => void;
  sxProps?: object;
  getRowClassName?: ((parameters: GridRowClassNameParams) => string) | undefined;
  onSortModelChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
};

export const DataTable = <T,>({
  columns,
  rows,
  pageSizeOptions = [5, 10],
  pageSize,
  totalCount,
  selectedRows,
  handleSelectionChange,
  handlePageChange,
  handleRowsPerPageChange,
  paginationMode = 'client',
  sortingMode = 'client',
  visibleColumns = {},
  setVisiblecolumns,
  selected = true,
  onRowClick,
  sxProps = {},
  getRowClassName,
  onSortModelChange,
}: DataTableProps<T>) => {
  const screenSize = useWindowSize();
  const rowSelectionModelChange = (newRowSelectionModel: GridRowSelectionModel) => {
    if (handleSelectionChange) {
      handleSelectionChange(newRowSelectionModel.map((rowId) => rowId.toString()));
    }
  };

  const handlePaginationModelChange = (parameters: GridPaginationModel) => {
    if (handlePageChange && handleRowsPerPageChange) {
      handlePageChange(parameters.page);
      handleRowsPerPageChange(parameters.pageSize);
    }
  };

  return (
    <DataGrid
      onSortModelChange={onSortModelChange}
      disableVirtualization
      onRowClick={(parameters) => onRowClick?.(parameters.row)}
      autoHeight
      disableColumnMenu
      disableRowSelectionOnClick
      rows={rows}
      columns={columns}
      initialState={{
        pagination: {
          paginationModel: { page: 0, pageSize: pageSize ?? pageSizeOptions[0] },
        },
      }}
      paginationMode={paginationMode}
      sortingMode={sortingMode}
      onPaginationModelChange={handlePaginationModelChange}
      rowCount={totalCount}
      onRowSelectionModelChange={rowSelectionModelChange}
      rowSelectionModel={selectedRows || []}
      pageSizeOptions={pageSizeOptions}
      checkboxSelection={selected}
      columnVisibilityModel={visibleColumns}
      onColumnVisibilityModelChange={(newModel) => setVisiblecolumns?.(newModel)}
      sx={sxProps}
      getRowClassName={getRowClassName}
      slotProps={{
        baseIconButton: { disableRipple: true },
        baseButton: { disableRipple: true },
        baseCheckbox: { disableRipple: true },
      }}
      rowHeight={screenSize.width && screenSize.width <= 1728 ? 44 : undefined}
      columnHeaderHeight={screenSize.width && screenSize.width <= 1728 ? 44 : undefined}
    />
  );
};
